/*
  Judge.me helpers, shared by the Reviews page (reviews-filter.js) and the robot pages' reviews block.
  - BBJudgeme.ready(): Judge.me only loads its product review widget code on pages that start with a product
    widget. This loads it on first use, from the same folder as the Judge.me loader already on the page (the
    folder changes with Judge.me's version), then waits for its code to finish setting up (max 8 s).
  - BBJudgeme.openForm(root): sets up the product widget inside root and opens its own "Write a review" form,
    so a review is always written for that robot.
  - <review-write>: the robot pages' "Write a review" button. It shows the hidden widget for this robot
    (sections/product-reviews.liquid) and opens the form, instead of linking to the Reviews page.
*/
window.BBJudgeme = window.BBJudgeme || {
  // The product widget needs two parts of Judge.me: widget_main.js (reviews) and widget_form.js (the form)
  ready() {
    const j = () => window.jdgm || {};
    const isReady = () => typeof j()._setupLoadReviewsEventsFor === 'function' && typeof j()._renderAndSetupReviewForm === 'function';
    if (isReady()) return Promise.resolve();
    if (this.loading) return this.loading;
    const loader = [...document.scripts].find((s) => /judgeme.*\/loader\.js/.test(s.src));
    if (!loader) return Promise.reject(new Error('Judge.me loader not found'));
    const load = (file) => new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = loader.src.replace(/loader\.js.*$/, file);
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    this.loading = (async () => {
      if (typeof j()._setupLoadReviewsEventsFor !== 'function') await load('widget_main.js');
      const started = Date.now();
      let formLoaded = false;
      while (!isReady()) {
        // widget_main.js usually pulls in the form code itself; if it hasn't after 1.5 s, load it here
        if (!formLoaded && Date.now() - started > 1500 && typeof j()._renderAndSetupReviewForm !== 'function') {
          formLoaded = true;
          await load('widget_form.js');
        }
        if (Date.now() - started > 8000) throw new Error('Judge.me widget code did not load');
        await new Promise((r) => setTimeout(r, 100));
      }
    })();
    this.loading.catch(() => { this.loading = null; });
    return this.loading;
  },

  // Sets up every Judge.me widget on the page. If Judge.me's setup throws, its pre-rendered reviews still show.
  init(root) {
    try {
      window.jdgm.initializeWidgets();
    } catch (err) {
      console.warn('Judge.me setup', err);
      root?.querySelectorAll('.jdgm-temp-hiding-style').forEach((st) => st.remove());
    }
  },

  async openForm(root) {
    await this.ready();
    this.init(root);
    await new Promise((r) => setTimeout(r, 60));
    const link = root.querySelector('.jdgm-write-rev-link');
    if (!link) throw new Error('No Judge.me write link');
    const form = root.querySelector('.jdgm-form-wrapper');
    if (!form || form.offsetParent === null) link.click();
    await new Promise((r) => setTimeout(r, 60));
    const target = root.querySelector('.jdgm-form-wrapper') || root;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    target.querySelector('input, textarea, [tabindex]')?.focus({ preventScroll: true });
  },
};

class ReviewWrite extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-review-write-panel]');
    const button = this.querySelector('[data-review-write]');
    button?.addEventListener('click', async (e) => {
      e.preventDefault();
      this.panel.hidden = false;
      try {
        await window.BBJudgeme.openForm(this.panel);
      } catch (err) {
        console.warn('Write a review: opening the Reviews page instead', err);
        window.location.href = button.dataset.fallback;
      }
    });
  }
}

if (!customElements.get('review-write')) customElements.define('review-write', ReviewWrite);
